/**
 * Runtime-environment-config degraded-reason vocabulary for the `DegradedOutcome` branches of the three leaves that
 * RESOLVE or PROJECT the app's environment config -- `components/lib/environment.ts` (server-side resolution),
 * `components/lib/client-runtime-config.ts` (client-safe projection) and
 * `components/auth/utils/firebase-config-lazy.ts` (the Firebase slice of the same config). The `cause` field carries
 * the thrown error, except on OPTIONAL_CONFIG_FIELDS_EMPTY where it carries the empty field list and on
 * `buildServerConfig`'s build-phase ENV_CONFIG_UNAVAILABLE, where nothing was thrown and it is absent.
 *  - OPTIONAL_CONFIG_FIELDS_EMPTY: PRODUCTION resolution passed every critical-field integrity check but one or more
 *    OPTIONAL fields resolved empty. Reachable only on the production branch of `buildServerConfig`; the config is
 *    still returned as the degraded value because an empty optional field disables a feature rather than the app.
 *  - ENV_CONFIG_UNAVAILABLE: the Next build phase, when runtime env vars and the shell-injected
 *    `window.__ENV_CONFIG__` are both absent. Two producers answer it: `buildServerConfig`, which returns the unbound
 *    (empty) config rather than run the live render's integrity check, and the client-safe projection, which returns
 *    build-time defaults when the INJECTED environment reader throws.
 *  - FIREBASE_CONFIG_LOAD_FAILED: the same injected reader threw while the Firebase slice was being projected, so
 *    empty-string Firebase defaults are returned. Distinct from ENV_CONFIG_UNAVAILABLE rather than a synonym: the
 *    degraded VALUE is a `FirebaseConfig`, not a `ClientRuntimeConfig`, and the memoized lazy accessor caches the
 *    outcome, so an operator seeing this token knows the empty config is sticky for the page's lifetime.
 *
 * ONE LEAF FOR THE THREE because they are one domain read three ways -- resolve, project client-safe, project the
 * Firebase slice -- and the three tokens are one vocabulary a reader triages together. Homed here rather than as bare
 * literals at each `degradedOutcome` call, which is the shape all three carried. Same leaf shape as every other
 * domain's (`const/vectorize-degraded-reasons.ts`, `const/service-bindings-degraded-reasons.ts`, ...).
 */

export const RUNTIME_CONFIG_DEGRADED_REASONS = {
	OPTIONAL_CONFIG_FIELDS_EMPTY: 'optional_config_fields_empty',
	ENV_CONFIG_UNAVAILABLE: 'env_config_unavailable',
	FIREBASE_CONFIG_LOAD_FAILED: 'firebase_config_load_failed',
} as const;

// NO DERIVED UNION EXPORT: two of the three producers default the `reason` type parameter, and the one that does NOT
// -- `getFirebaseConfig` -- annotates with its OWN member type
// (`typeof RUNTIME_CONFIG_DEGRADED_REASONS.FIREBASE_CONFIG_LOAD_FAILED`), which is narrower than a union over all
// three and is the contract its callers actually receive. A union here would be a type no consumer can be handed.
